import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { AppConfigContext } from '@config/AppConfig';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { EPermitToLoadStatus } from '@permitToLoadRemove/permitToLoadRemove.models';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import { EConstructionRemovalCertPermission, ERolePermission } from '@userProfile/permission/permission.constants';
import {
  hasAtLeastOnePermissionsInArray,
  isPermittedAsSystemUser,
  isPermittedAsSiteOperationUser,
} from '@userProfile/permission/permission.services';
import { useContext, useMemo } from 'react';
import { ProjectSettingsAttribute } from '@projectSettings/projectSettings.models';
import { isProjectSettingAttributeActive } from '@projectSettings/projectSettings.services';
import { useBelongToSiteOperationsCompany, useNominatedInMasterSchedule } from '@masterSchedule/masterSchedule.hooks';
import { IRolePermissionFlattened } from '@userProfile/permission/permission.models';
import { useProjectIsArchived } from '@config/config.hooks';
import { IProjectLoadingRemovalCert } from './permitToLoadRemoveDetailPage.models';

export const isSupersededT4 = (currentT4?: IProjectLoadingRemovalCert) => {
  return !(currentT4?.isLatestRevision ?? true);
};

export interface IT4TopBtnsVisibility {
  isShowSaveBtn: boolean;
  isShowIssueToTwcBtn: boolean;
  isShowApproveBtn: boolean;
  isShowCancelBtn: boolean;
  isShowRemoveRemovalCertBtn: boolean;
  isShowCreateRevisionBtn: boolean;
  isShowCreateUnloadBtn: boolean;
  isShowPrintBtn: boolean;
  isShowPrintIceCert: boolean;
  isShowPrintIceCertWithoutSign: boolean;
  isShowPrintIceCertRemovalCert: boolean;
  isShowPrintIceCertRemovalCertWithoutSign: boolean;
  isShowGenerateIceCertificate: boolean;
  isShowWithdrawIceCertificateRequest: boolean;
  isShowSelectTwcBtn: boolean;
}
export const useT4TopBtnsVisibility: (
  isSuperseded: boolean,
  mode: DetailPageAccessMode,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => IT4TopBtnsVisibility = (isSuperseded, mode, loadingRemovalCert, masterSchedule) => {
  const isShowSaveBtn = useIsShowSaveBtn(loadingRemovalCert, masterSchedule);
  const isShowIssueToTwcBtn = useIsShowIssueToTwcBtn(isSuperseded, loadingRemovalCert, masterSchedule);
  const isShowApproveBtn = useIsShowApproveBtn(loadingRemovalCert, masterSchedule);
  const isShowGenerateIceCertificate = useIsShowGenerateIceCertificate(loadingRemovalCert, masterSchedule);
  const isShowWithdrawIceCertificateRequest = useIsShowWithdrawIceCertificateRequest(
    loadingRemovalCert,
    masterSchedule,
  );
  const isShowCancelBtn = useIsShowCancelBtn(loadingRemovalCert, masterSchedule);
  const isShowCreateRevisionBtn = useIsShowCreateRevisionBtn(isSuperseded, loadingRemovalCert, masterSchedule);
  const isShowRemoveRemovalCertBtn = useIsShowRemoveRemovalCertBtn(isSuperseded, loadingRemovalCert, masterSchedule);
  const isShowCreateUnloadBtn = useIsShowCreateUnloadBtn(loadingRemovalCert, masterSchedule);
  const isShowSelectTwcBtn = useIsShowSelectTwcBtn(isSuperseded, mode, loadingRemovalCert, masterSchedule);

  const isShowPrintBtn = useIsShowPrintBtn(isSuperseded, loadingRemovalCert);
  const isShowPrintIceCert = useIsShowPrintIceCert(isSuperseded, loadingRemovalCert);
  const isShowPrintIceCertWithoutSign = useIsShowPrintIceCertWithoutSign(loadingRemovalCert);
  const isShowPrintIceCertRemovalCert = useIsShowPrintIceCertRemovalCert(isSuperseded, loadingRemovalCert);
  const isShowPrintIceCertRemovalCertWithoutSign = useIsShowPrintIceCertRemovalCertWithoutSign(
    isSuperseded,
    loadingRemovalCert,
  );
  return {
    isShowSaveBtn,
    isShowIssueToTwcBtn,
    isShowApproveBtn,
    isShowGenerateIceCertificate,
    isShowWithdrawIceCertificateRequest,
    isShowCancelBtn,
    isShowRemoveRemovalCertBtn,
    isShowCreateRevisionBtn,
    isShowCreateUnloadBtn,
    isShowSelectTwcBtn,
    isShowPrintBtn,
    isShowPrintIceCert,
    isShowPrintIceCertWithoutSign,
    isShowPrintIceCertRemovalCert,
    isShowPrintIceCertRemovalCertWithoutSign,
  };
};

// #region Status btns

const useIsShowIssueToTwcBtn = (
  isSuperseded: boolean,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );

  const { isSrp } = useNominatedInMasterSchedule(masterSchedule);

  const isPermitted = isPermittedT4Init(appConfigs.configs.myPermissions);

  if (isSuperseded) return false;
  if (!isSrp) return false;

  if (
    !loadingRemovalCert ||
    loadingRemovalCert?.status === EPermitToLoadStatus.INITIAL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REJECTED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REMOVAL_REJECTED
  ) {
    return isPermitted;
  }

  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    if (isMultipleUnloadCert) {
      if (
        loadingRemovalCert?.isRemovalCert &&
        loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL
      ) {
        return isPermitted;
      }
      return false;
    }
    return true;
  }

  return false;
};

const useIsShowApproveBtn = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isTwc, isTwcm, isNominatedChecker } = useNominatedInMasterSchedule(masterSchedule);

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );

  const isPermittedTwcmEndorsed =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWCM_ENDORSED,
    ]) ?? false;
  const isPermittedTwcEndorsed =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWC_ENDORSED,
    ]) ?? false;

  const isRiskCatCOrD =
    masterSchedule?.riskCategory?.category === ERiskCategory.D ||
    masterSchedule?.riskCategory?.category === ERiskCategory.C;
  const notSignAsSrp =
    loadingRemovalCert?.siteEngineerProjectUserProfileId !== appConfigs.configs.myProjectUserProfile?.id;

  const isPermittedWithRiskCat = () => {
    if (!notSignAsSrp) return false;
    if (isRiskCatCOrD && (isTwc || isTwcm)) return isPermittedTwcmEndorsed || isPermittedTwcEndorsed;

    if (!isRiskCatCOrD && isTwc) return isPermittedTwcEndorsed;
    return false;
  };

  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC) {
    return isPermittedWithRiskCat();
  }

  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    if (!isMultipleUnloadCert) return false;
    if (
      loadingRemovalCert?.isRemovalCert &&
      loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL
    ) {
      return isPermittedWithRiskCat();
    }
  }

  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL) {
    return isPermittedWithRiskCat();
  }

  if (isWaitingIce(loadingRemovalCert)) {
    return (
      isNominatedChecker &&
      hasAtLeastOnePermissionsInArray(
        appConfigs.configs.myPermissions,
        [EConstructionRemovalCertPermission.ICE_APPROVAL, ERolePermission.ICE_AUTHORISED_REPRESENTATIVE],
        masterSchedule?.responsibleChecker.companyTypeId,
        masterSchedule?.responsibleChecker.organizationTypeId,
      )
    );
  }
  return false;
};

const useIsShowGenerateIceCertificate = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);

  if (!isTwc && !isTwcm) return false;

  const requiresICEConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICEConstructionCert,
  );
  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  const isPermittedTwc = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions ?? [], [
    ERolePermission.TEMPORARY_WORK_COORDINATOR,
  ]);

  const isPermittedIssueIce =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.ISSUE_TO_ICE,
    ]) ?? false;

  const isRiskCatCOrD =
    masterSchedule?.riskCategory?.category === ERiskCategory.D ||
    masterSchedule?.riskCategory?.category === ERiskCategory.C;

  if (!isPermittedIssueIce) return false;

  if (
    !loadingRemovalCert?.isRemovalCert &&
    loadingRemovalCert?.removalCertRequired &&
    (loadingRemovalCert.projectRemovalCerts?.length ?? 0) > 0
  )
    return false;
  if (requiresICEConstructionCert) {
    if (loadingRemovalCert?.iceSignatureRequired) return false;
    if (
      loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL &&
      (!isMultipleUnloadCert || !loadingRemovalCert.isRemovalCert)
    )
      return isRiskCatCOrD ? true : isPermittedTwc;

    if (
      loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED &&
      !loadingRemovalCert.isRemovalCert &&
      !loadingRemovalCert.removalCertRequired
    )
      return isRiskCatCOrD ? true : isPermittedTwc;
  }

  if (isGenerateRemovalCert(loadingRemovalCert)) return isRiskCatCOrD ? true : isPermittedTwc;

  return false;
};

const useIsShowWithdrawIceCertificateRequest = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);

  if (!isTwc && !isTwcm) return false;
  if (isWaitingIce(loadingRemovalCert))
    return (
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
        EConstructionRemovalCertPermission.ISSUE_TO_ICE,
      ]) ?? false
    );

  return false;
};

const useIsShowCreateUnloadBtn = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);

  if (loadingRemovalCert?.isRemovalCert) return false;

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  if (!isMultipleUnloadCert) return false;
  if (loadingRemovalCert?.status !== EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) return false;
  return (isSrp || isTwc || isTwcm) && isPermittedT4Init(appConfigs.configs.myPermissions);
};

const useIsShowSaveBtn = (loadingRemovalCert?: IProjectLoadingRemovalCert, masterSchedule?: IMasterScheduleDetail) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);
  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  if (isProjectArchived || masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;
  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL
  ) {
    return false;
  }

  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    if (isMultipleUnloadCert) {
      if (
        loadingRemovalCert?.isRemovalCert &&
        loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL
      ) {
        return false;
      }
    }
  }

  return (isSrp || isTwc || isTwcm) && isPermittedT4Init(appConfigs.configs.myPermissions);
};

const useIsShowCancelBtn = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isSrp, isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (!isSrp && !isTwc) return false;
  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL
  )
    return false;
  return isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EConstructionRemovalCertPermission.CANCEL]);
};

const useIsShowCreateRevisionBtn = (
  isSuperseded: boolean,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (isSuperseded) return false;
  if (!isTwc) return false;

  const requiresICEConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICEConstructionCert,
  );
  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );

  const isPermitted =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.T4_CREATE_REVISION,
    ]) ?? false;
  if (!isPermitted) return false;

  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC) return true;

  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL)
    if (isMultipleUnloadCert)
      if (
        loadingRemovalCert?.isRemovalCert &&
        loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL
      ) {
        return true;
      }

  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL) {
    return true;
  }
  if (requiresICEConstructionCert) {
    if (loadingRemovalCert?.iceSignatureRequired) return false;
    if (
      loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL &&
      (!isMultipleUnloadCert || !loadingRemovalCert.isRemovalCert)
    )
      return true;

    if (
      loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED &&
      !loadingRemovalCert.isRemovalCert &&
      !loadingRemovalCert.removalCertRequired
    )
      return true;
  }

  if (isGenerateRemovalCert(loadingRemovalCert)) return true;

  if (isWaitingIce(loadingRemovalCert)) return true;

  return false;
};

const useIsShowRemoveRemovalCertBtn = (
  isSuperseded: boolean,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isSrp, isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (isSuperseded) return false;

  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.COMPLETED) return false;

  if (!loadingRemovalCert?.isRemovalCert) return false;

  if (loadingRemovalCert.isFinalRemovalCert) return false;

  if (
    loadingRemovalCert.status !== EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL &&
    loadingRemovalCert.status !== EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL
  )
    return false;

  return (
    (isSrp || isTwc) &&
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EConstructionRemovalCertPermission.CANCEL])
  );
};

const useIsShowSelectTwcBtn = (
  isSuperseded: boolean,
  mode: DetailPageAccessMode,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  const isPermittedInitate = isPermittedT4Init(appConfigs.configs.myPermissions);
  const { isSrp } = useNominatedInMasterSchedule(masterSchedule);

  const isRiskCatCOrD =
    masterSchedule?.riskCategory?.category === ERiskCategory.D ||
    masterSchedule?.riskCategory?.category === ERiskCategory.C;
  const nominatedTwc = isRiskCatCOrD
    ? [...(masterSchedule?.responsibleTwc ?? []), ...(masterSchedule.responsibleTwcm ?? [])]
    : masterSchedule?.responsibleTwc ?? [];
  const hvNominatedTwc = nominatedTwc.length > 0;

  if (isSuperseded) return false;
  if (!isSrp) return false;

  if (mode === 'Create' || loadingRemovalCert?.status === EPermitToLoadStatus.INITIAL) {
    if (!isPermittedInitate) return false;
    if (hvNominatedTwc) return true;
  }

  if (!isMultipleUnloadCert && loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    return hvNominatedTwc;
  }
  if (
    isMultipleUnloadCert &&
    loadingRemovalCert?.isRemovalCert &&
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL &&
    loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL
  ) {
    if (!isPermittedInitate) return false;
    return hvNominatedTwc;
  }

  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REJECTED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REMOVAL_REJECTED
  ) {
    if (!isPermittedInitate) return false;
    return hvNominatedTwc;
  }

  return false;
};

const isGenerateRemovalCert = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  const requiresICERemovalCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICERemovalCert,
  );

  if (requiresICERemovalCert) {
    if (!loadingRemovalCert?.isRemovalCert) return false;
    if (loadingRemovalCert.iceSignatureRequired) return false;
    if (loadingRemovalCert?.status !== EPermitToLoadStatus.COMPLETED) return false;

    if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
      if (isMultipleUnloadCert) {
        return true;
      }
    }
    return true;
  }
  return false;
};

const isWaitingIce = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    if (isMultipleUnloadCert) {
      if (
        loadingRemovalCert?.isRemovalCert &&
        loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL
      ) {
        return true;
      }
    }
  }
  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL
  ) {
    return true;
  }
  return false;
};

// #endregion Status btns

// #region Print btns
const useIsShowPrintBtn = (isSuperseded: boolean, loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  if (!loadingRemovalCert) return false;
  if (isSuperseded) return false;
  return true;
};

const useIsShowPrintIceCert = (isSuperseded: boolean, loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  if (isSuperseded) return false;
  // Only show Print ICE Cert button for loading certificates when ICE has signed
  if (!loadingRemovalCert?.iceProjectUserProfileId) return false;
  if (loadingRemovalCert?.isRemovalCert) return false;

  const requiresICEConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICEConstructionCert,
  );
  const showT4ConstCertWOICE = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.ShowT4ConstCertWOICE,
  );
  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );

  const isPermittedIssueToIce =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.ISSUE_TO_ICE,
    ]) ?? false;

  if (requiresICEConstructionCert) {
    if (!loadingRemovalCert?.iceSignatureRequired) {
      if (
        loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL &&
        (!isMultipleUnloadCert || !loadingRemovalCert?.isRemovalCert)
      ) {
        return isPermittedIssueToIce;
      }
      if (
        loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED &&
        !loadingRemovalCert.isRemovalCert &&
        !loadingRemovalCert.removalCertRequired
      ) {
        return isPermittedIssueToIce;
      }
    }
  }

  if (loadingRemovalCert?.iceSignatureRequired) {
    return true;
  }
  if (showT4ConstCertWOICE) {
    if (
      loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL ||
      loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED
    ) {
      return true;
    }
  }

  return false;
};
const useIsShowPrintIceCertWithoutSign = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  const NoSignConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.NoSignConstructionCert,
  );
  return (loadingRemovalCert?.iceSignatureRequired ?? false) && NoSignConstructionCert;
};

const useIsShowPrintIceCertRemovalCert = (isSuperseded: boolean, loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  if (isSuperseded) return false;

  // Only show Print ICE Removal Cert button for removal certificates when ICE has signed
  if (!loadingRemovalCert?.isRemovalCert) return false;
  if (!loadingRemovalCert?.iceProjectUserProfileId) return false;

  const requiresICEConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICEConstructionCert,
  );
  const showT4ConstCertWOICE = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.ShowT4ConstCertWOICE,
  );
  const isPermittedIssueToIce =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.ISSUE_TO_ICE,
    ]) ?? false;

  if (requiresICEConstructionCert) {
    if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL) return true;

    if (loadingRemovalCert?.isRemovalCert) {
      if (loadingRemovalCert.removalCertRequired && loadingRemovalCert.status === EPermitToLoadStatus.COMPLETED)
        return true;

      if (loadingRemovalCert.status !== EPermitToLoadStatus.COMPLETED && !loadingRemovalCert.iceSignatureRequired)
        return isPermittedIssueToIce;
    }
  }

  if (isGenerateRemovalCert(loadingRemovalCert)) return isPermittedIssueToIce;

  if (
    showT4ConstCertWOICE &&
    (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL ||
      loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED)
  )
    return true;
  return false;
};
const useIsShowPrintIceCertRemovalCertWithoutSign = (
  isSuperseded: boolean,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
) => {
  const appConfigs = useContext(AppConfigContext);

  if (isSuperseded) return false;

  const noSignConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.NoSignConstructionCert,
  );
  const requiresICEConstructionCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICEConstructionCert,
  );

  if (!noSignConstructionCert) return false;

  if (requiresICEConstructionCert) {
    if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE_REMOVAL) return true;

    if (loadingRemovalCert?.isRemovalCert) {
      if (loadingRemovalCert.removalCertRequired && loadingRemovalCert.status === EPermitToLoadStatus.COMPLETED)
        return true;
    }
  }
  if (isGenerateRemovalCert(loadingRemovalCert)) return true;

  return false;
};

// #endregion Print btns

export const useT4DetailIsReadOnly = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const isProjectArchived = useProjectIsArchived();
  if (isProjectArchived || masterSchedule?.isCancelled || masterSchedule?.isLocked) return true;
  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL
  )
    return true;

  return false;
};

export const useT4DetailFieldsEnabled = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const isProjectArchived = useProjectIsArchived();
  const isGeneralFieldsEnabled = useT4DetailGeneralFieldsEnabled(loadingRemovalCert, masterSchedule);
  const isPermitToRemoveRequiredEnable = useIsPermitToRemoveRequiredEnable(loadingRemovalCert, masterSchedule);
  const isFinalRemovalCertificateEnable = useIsFinalRemovalCertificateEnable(loadingRemovalCert, masterSchedule);
  const isLocationPlanEnabled = useIsLocationPlanEnabled(loadingRemovalCert, masterSchedule);
  const isChecklistEnabled = useIsChecklistEnabled(loadingRemovalCert, masterSchedule);
  const isOtherReferenceDocEnabled = useIsOtherReferenceDocEnabled(loadingRemovalCert, masterSchedule);
  const isOtherReferenceDocRemovalEnabled = useIsOtherReferenceDocRemovalEnabled(loadingRemovalCert, masterSchedule);
  return {
    isGeneralFieldsEnabled,
    isPermitToRemoveRequiredEnable,
    isFinalRemovalCertificateEnable,
    isLocationPlanEnabled: !isProjectArchived && isLocationPlanEnabled,
    isChecklistEnabled: !isProjectArchived && isChecklistEnabled,
    isOtherReferenceDocEnabled: !isProjectArchived && isOtherReferenceDocEnabled,
    isOtherReferenceDocRemovalEnabled: !isProjectArchived && isOtherReferenceDocRemovalEnabled,
  };
};

const useIsPermitToRemoveRequiredEnable = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  if (masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;
  if (
    !loadingRemovalCert ||
    loadingRemovalCert?.status === EPermitToLoadStatus.INITIAL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC ||
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REJECTED
  ) {
    return true;
  }

  return false;
};

const useIsFinalRemovalCertificateEnable = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );
  const isPermittedInitate = isPermittedT4Init(appConfigs.configs.myPermissions);
  const { isSrp } = useNominatedInMasterSchedule(masterSchedule);

  if (masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;
  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    if (isMultipleUnloadCert && loadingRemovalCert.isRemovalCert && isSrp && isPermittedInitate) {
      return true;
    }
  }
  return false;
};

const useT4DetailGeneralFieldsEnabled = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const isShowSaveBtn = useIsShowSaveBtn(loadingRemovalCert, masterSchedule);
  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL ||
    (loadingRemovalCert?.removalCertRequired &&
      loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE
  )
    return false;

  return isShowSaveBtn;
};

const useIsLocationPlanEnabled = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  if (masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;

  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REMOVAL_REJECTED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE ||
    loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL ||
    (loadingRemovalCert?.removalCertRequired &&
      loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL)
  )
    return false;

  return true;
};

const useIsChecklistEnabled = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  if (masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;

  if (
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.TWC_REMOVAL_REJECTED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ICE ||
    loadingRemovalCert?.status === EPermitToLoadStatus.COMPLETED ||
    loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL ||
    (loadingRemovalCert?.removalCertRequired &&
      loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL)
  )
    return false;

  return true;
};

const useIsOtherReferenceDocEnabled = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  if (masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;

  if (
    !loadingRemovalCert?.status ||
    loadingRemovalCert?.status === EPermitToLoadStatus.INITIAL ||
    loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC
  )
    return true;
  return false;
};
const useIsOtherReferenceDocRemovalEnabled = (
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);

  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );

  const isPermittedInit = isPermittedT4Init(appConfigs.configs.myPermissions);

  const isPermittedTwcmEndorsed =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWCM_ENDORSED,
    ]) ?? false;
  const isPermittedTwcEndorsed =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWC_ENDORSED,
    ]) ?? false;

  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);

  if (masterSchedule?.isCancelled || masterSchedule?.isLocked) return false;

  const notSignAsSrp =
    loadingRemovalCert?.siteEngineerProjectUserProfileId !== appConfigs.configs.myProjectUserProfile?.id;
  const isRiskCatCOrD =
    masterSchedule?.riskCategory?.category === ERiskCategory.D ||
    masterSchedule?.riskCategory?.category === ERiskCategory.C;

  if (loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) {
    if (isMultipleUnloadCert) {
      if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) return isPermittedInit && isSrp;

      if (loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL && notSignAsSrp) {
        if (isRiskCatCOrD) return isPermittedTwcmEndorsed && (isTwc || isTwcm);
        return isPermittedTwcEndorsed && isTwc;
      }
    }

    return isSrp;
  }

  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL) {
    if (!notSignAsSrp) return false;
    if (isRiskCatCOrD) return isPermittedTwcmEndorsed && (isTwc || isTwcm);
    return isPermittedTwcEndorsed && isTwc;
  }

  return false;
};

export const useT4PermitsVisibility = (
  isSuperseded: boolean,
  loadingRemovalCert?: IProjectLoadingRemovalCert,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const isReadOnly = masterSchedule?.isCancelled || masterSchedule?.isLocked;
  const isShowPermitToRemoveIce = useIsShowPermitToRemoveIce(loadingRemovalCert);
  const isEnableLoadingCertTwcComment = useIsEnableLoadingCertTwcComment(loadingRemovalCert);
  const isEnableRemovalCertTwcComment = useIsEnableRemovalCertTwcComment(isSuperseded, loadingRemovalCert);
  const isEnableLoadingCertIceComment = useIsEnableLoadingCertIceComment(loadingRemovalCert);
  const isEnableRemovalCertIceComment = useIsEnableRemovalCertIceComment(loadingRemovalCert);
  return {
    isShowPermitToRemoveIce: !isReadOnly && isShowPermitToRemoveIce,
    isEnableLoadingCertTwcComment: !isReadOnly && isEnableLoadingCertTwcComment,
    isEnableRemovalCertTwcComment: !isReadOnly && isEnableRemovalCertTwcComment,
    isEnableLoadingCertIceComment: !isReadOnly && isEnableLoadingCertIceComment,
    isEnableRemovalCertIceComment: !isReadOnly && isEnableRemovalCertIceComment,
  };
};

const useIsShowPermitToRemoveIce = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  const requiresICERemovalCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.RequiresICERemovalCert,
  );
  return (
    requiresICERemovalCert &&
    loadingRemovalCert?.removalCertRequired &&
    loadingRemovalCert.isRemovalCert &&
    loadingRemovalCert.iceSignatureRequired
  );
};

const useIsEnableLoadingCertTwcComment = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);

  if (loadingRemovalCert?.status !== EPermitToLoadStatus.WAITING_FOR_TWC) return false;
  return (
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWC_COMMENT,
    ]) || isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EConstructionRemovalCertPermission.TWC_COMMENT])
  );
};

const useIsEnableRemovalCertTwcComment = (isSuperseded: boolean, loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const appConfigs = useContext(AppConfigContext);
  const isMultipleUnloadCert = isProjectSettingAttributeActive(
    appConfigs.configs.project?.projectSettings,
    ProjectSettingsAttribute.EnableMultipleUnloadCert,
  );

  const isPermitted =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWC_COMMENT,
    ]) || isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EConstructionRemovalCertPermission.TWC_COMMENT]);

  if (isSuperseded) return false;

  if (
    isMultipleUnloadCert &&
    loadingRemovalCert?.isRemovalCert &&
    loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL &&
    loadingRemovalCert?.projectLoadCert?.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL
  )
    return isPermitted;

  if (loadingRemovalCert?.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL) return isPermitted;

  return false;
};
const useIsEnableLoadingCertIceComment = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  if (!loadingRemovalCert?.iceSignatureRequired) return false;
  if (loadingRemovalCert.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL) return false;
  // TODO: if (objConstructionRemovalCert.ICEName === "*UPLOAD*") return false
  return true;
};
const useIsEnableRemovalCertIceComment = (loadingRemovalCert?: IProjectLoadingRemovalCert) => {
  const isShowPermitToRemoveIce = useIsShowPermitToRemoveIce(loadingRemovalCert);
  if (!isShowPermitToRemoveIce) return false;
  // TODO: if (objConstructionRemovalCert.ICEName === "*UPLOAD*") return false
  return true;
};

export const useT4CommentPermission = (masterSchedule?: IMasterScheduleDetail) => {
  const appConfigs = useContext(AppConfigContext);

  const hasCommentPermission =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EConstructionRemovalCertPermission.TWC_COMMENT,
    ]) ||
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EConstructionRemovalCertPermission.TWC_COMMENT]);
  const hasResponsePermission = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    ERolePermission.SITE_ENGINEER,
  ]);

  const nominated = useNominatedInMasterSchedule(masterSchedule);

  const hasCommentRight = useMemo(() => {
    return (nominated.isSrp || nominated.isTwc || nominated.isTwcm) && hasCommentPermission;
  }, [JSON.stringify(nominated), hasCommentPermission]);

  const hasResponseRight = useMemo(() => {
    return hasResponsePermission && nominated.isSrp;
  }, [JSON.stringify(nominated), hasResponsePermission]);

  const isReadOnly = useMemo(() => {
    if (hasCommentRight || hasResponseRight) return false;
    return nominated.isNominatedChecker || nominated.isNominatedDesigner;
  }, [hasCommentRight, hasResponseRight, JSON.stringify(nominated)]);

  const isBelongToSiteOperationsCompany = useBelongToSiteOperationsCompany(masterSchedule?.projectId as number);
  const canViewDraftComment = hasCommentRight && isBelongToSiteOperationsCompany;

  return { isReadOnly, hasCommentRight, hasResponseRight, canViewDraftComment };
};

const isPermittedT4Init = (permissions: IRolePermissionFlattened[] = []) => {
  return (
    isPermittedAsSiteOperationUser(permissions, [EConstructionRemovalCertPermission.INITATE]) ||
    isPermittedAsSystemUser(permissions, [EConstructionRemovalCertPermission.INITATE])
  );
};
